import { Suspense, useEffect, useState } from "react";
import { useToastContext } from "../../../../../common/components/ui/toast/toast.context";
import {
  IWorkOrder,
  WORK_ORDER_TYPE,
} from "../../../../../types/workorder.types";
import {
  TableFilters,
  TableFiltersState,
} from "../../../../../common/components/ui/table/TableFilters";
import { getPreparationReportsWorkOrder } from "../../../../../common/services/workOrder.api";
import Loading from "../../../../../common/components/ui/loading/Loading";
import GenericTable from "../../../../../common/components/ui/table/GenericTable";
import TableFooter from "../../../../../common/components/ui/table/TableFooter";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../../../../../common/helpers/date.helpers";
import { newMaskCurrency } from "../../../../../common/helpers/mask.helpers";
import { useCompanyStore } from "@/common/states/store/Company";
import { getCompanySigla } from "@/common/helpers/company.helpers";

const TYPE_LABELS = {
  [WORK_ORDER_TYPE.PREPARATION]: "Preparação",
  [WORK_ORDER_TYPE.REPREPARATION]: "Repreparação",
};

function getTypeLabels(type: WORK_ORDER_TYPE) {
  return TYPE_LABELS[type] || type;
}

const headers = {
  companyId: "Cidade",
  createdAt: "Data",
  licensePlate: "Placa",
  partnerName: "Parceiro",
  type: "Tipo",
  totalSellPrice: "Total",
};

function TablePreparation() {
  const { notifyError } = useToastContext();
  const navigate = useNavigate();

  const itemsPerPage = 10;

  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [workOrder, setWorkOrder] = useState<IWorkOrder[]>([]);
  const [data, setData] = useState({ itemsPerPage, totalItems: 0 });
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
  });

  const { company } = useCompanyStore();
  const currentUF = useCompanyStore((state) => state.selectedUf);

  useEffect(() => {
    async function loadWorkOrder() {
      try {
        setIsLoading(true);
        setWorkOrder([]);
        const result = await getPreparationReportsWorkOrder({
          ...filters,
          page,
          itemsPerPage,
        });

        if (!result.ok) {
          notifyError(
            (result as { message?: string })?.message || "Erro desconhecido"
          );
          return;
        }

        setWorkOrder(result.data as IWorkOrder[]);
        setData({ itemsPerPage, totalItems: result.totalItems });
      } catch (err) {
        notifyError("Erro ao buscar dados.");
      } finally {
        setIsLoading(false);
      }
    }
    loadWorkOrder();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, itemsPerPage, company, currentUF]);

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };
  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Relatório de Preparações"
          placeholder="Placa"
          statusOptions={TYPE_LABELS}
          onNewItemClick={() => {}}
          onSearch={handleFilterChange}
          entity="inventory-workorder"
          tableData={workOrder}
        />

        <GenericTable
          headers={headers}
          data={workOrder}
          renders={{
            companyId: (companyId) => {
              return getCompanySigla(companyId as string);
            },
            createdAt: formatDate,
            licensePlate: (licensePlate, workOrder) => (
              <a
                href={`/relatorios/preparacoes/${
                  (workOrder as IWorkOrder)._id
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(
                    `/relatorios/preparacoes/${(workOrder as IWorkOrder)._id}`
                  );
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {licensePlate as string}
              </a>
            ),
            partnerName: (partnerName) => {
              const name = partnerName as string;
              if (!name) return "-";
              const encodedPartnerName = encodeURIComponent(name);

              return (
                <a
                  href={`/relatorios/preparacoes/details/${encodedPartnerName}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(
                      `/relatorios/preparacoes/details/${encodedPartnerName}`
                    );
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {name}
                </a>
              );
            },
            type: (type) => getTypeLabels(type as WORK_ORDER_TYPE),
            totalSellPrice: (totalSellPrice) =>
              newMaskCurrency(totalSellPrice as number),
          }}
          entity="inventory-workorder"
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page}
          totalItems={data.totalItems}
          itemsPerPage={itemsPerPage}
          goToPage={setPage}
        />
      </div>
    </Suspense>
  );
}

export default TablePreparation;
